/**
 * Master Inventory Table Component
 * Matches CapsInventory UI exactly
 */

import React, { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
  Collapse, Chip, IconButton, Stack, alpha, useTheme, Tooltip, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Edit as EditIcon, Description as DescriptionIcon,
  Receipt as ReceiptIcon, AssignmentTurnedIn as AssignmentTurnedInIcon,
  Science as ScienceIcon, LocalShipping as LocalShippingIcon, Warning as WarningIcon,
  CheckCircle as CheckCircleIcon, Search as SearchIcon, Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { formatDate, formatQuantity } from './inventoryUtils';
import EditLotDialog from './EditLotDialog';
import EditReorderDialog from './EditReorderDialog';
import PaginationController from './PaginationController';
import { supabase } from '../../../../supabaseClient';

const LOT_BATCH_DEFAULT_ROWS = 12;

/** Clean notes: strip out system-generated adjustment/transfer references, only show user-written notes */
function cleanNotes(notes) {
  if (!notes) return '';
  // Split on semicolons and filter out system-generated segments
  const segments = notes.split(';').map(s => s.trim()).filter(Boolean);
  const cleaned = segments.filter(seg => {
    // Filter out ugly system-generated notes
    if (seg.match(/^Adjusted to \d+/)) return false;
    if (seg.startsWith('Transfer merge - Added')) return false;
    if (seg.startsWith('Transfer from location - Ref:')) return false;
    if (seg.startsWith('Adjustment created lot/batch - Ref:')) return false;
    if (seg.match(/^Merged transfer from /)) return false;
    return true;
  });
  return cleaned.join('; ');
}

export default function InventoryTable({ data = [], config, inventoryType, expandable = false, onRefresh, noContainerBorder = false, canEdit = true, canDelete = true }) {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState({});
  const [lotBatchSearchTerms, setLotBatchSearchTerms] = useState({});
  const [lotBatchPagination, setLotBatchPagination] = useState({});
  const [editLotDialogOpen, setEditLotDialogOpen] = useState(false);
  const [editReorderDialogOpen, setEditReorderDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteLotDialogOpen, setDeleteLotDialogOpen] = useState(false);
  const [deleteAggregateDialogOpen, setDeleteAggregateDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleRowClick = (itemId) => {
    if (!expandable) return;
    setExpandedRows(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleDeleteLot = (lotItem) => {
    setItemToDelete(lotItem);
    setDeleteLotDialogOpen(true);
  };

  const handleDeleteAggregate = (item) => {
    setItemToDelete(item);
    setDeleteAggregateDialogOpen(true);
  };

  const confirmDeleteLot = async () => {
    if (!itemToDelete) return;

    try {
      setDeleting(true);

      // STEP 1: Purge ANY inactive records with same lot/batch to prevent reuse
      await supabase
        .from(config.itemsTable)
        .delete()
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, itemToDelete[config.itemIdField])
        .eq('location_id', itemToDelete.location_id)
        .eq('lot_number', itemToDelete.lot_number)
        .eq('batch_number', itemToDelete.batch_number)
        .eq('is_active', false);

      // STEP 2: Fetch source transaction to capture lot/batch, then delete it
      let sourceLotNumber = itemToDelete.lot_number || null;
      let sourceBatchNumber = itemToDelete.batch_number || null;
      if (itemToDelete.source_transaction_id) {
        const { data: sourceTx } = await supabase
          .from(config.transactionsTable)
          .select('lot_number, batch_number')
          .eq('id', itemToDelete.source_transaction_id)
          .single();
        if (sourceTx) {
          sourceLotNumber = sourceTx.lot_number || sourceLotNumber;
          sourceBatchNumber = sourceTx.batch_number || sourceBatchNumber;
        }
        const { error: txError } = await supabase
          .from(config.transactionsTable)
          .delete()
          .eq('id', itemToDelete.source_transaction_id);
        if (txError) {
          console.warn('Failed to delete source transaction:', txError);
        }
      }

      // STEP 3: Log deletion as a transaction
      // Use current_quantity (normalized to current_unit_quantity for chemicals in useInventoryData)
      const deletedQty  = Math.abs(parseFloat(itemToDelete.current_quantity) || 0);
      const deletedTotalQty = Math.abs(parseFloat(itemToDelete.current_total_quantity) || deletedQty);
      const itemName = itemToDelete[config.tableName]?.name || itemToDelete.item_name || 'item';
      const lotParts = [
        sourceLotNumber ? `Lot: ${sourceLotNumber}` : null,
        sourceBatchNumber ? `Batch: ${sourceBatchNumber}` : null
      ].filter(Boolean).join(', ');
      // Chemicals use unit_quantity + total_quantity; simple types use quantity
      const txQtyFields = config.hasUnitOfMeasure
        ? { unit_quantity: -deletedQty, total_quantity: -deletedTotalQty }
        : { quantity: -deletedQty };
      const { error: txLogError } = await supabase.from(config.transactionsTable).insert({
        company_id: itemToDelete.company_id,
        [config.itemIdField]: itemToDelete[config.itemIdField],
        transaction_type: 'inventory_deletion',
        ...txQtyFields,
        location_id: itemToDelete.location_id,
        lot_number: sourceLotNumber,
        batch_number: sourceBatchNumber,
        transaction_date: new Date().toISOString(),
        notes: `Lot deleted — ${itemName}${lotParts ? ` (${lotParts})` : ''}`
      });
      if (txLogError) {
        console.error('Failed to log deletion transaction:', txLogError);
      }

      // STEP 4: Delete from items table
      const { error } = await supabase
        .from(config.itemsTable)
        .delete()
        .eq('id', itemToDelete.id);

      if (error) throw error;

      // STEP 5: Recalculate aggregate
      await updateAggregateAfterDelete(itemToDelete);

      if (onRefresh) await onRefresh();
      setDeleteLotDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting lot:', error);
      alert('Failed to delete lot/batch: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteAggregate = async () => {
    if (!itemToDelete) return;

    try {
      setDeleting(true);

      const actualItemId = itemToDelete.item_id || itemToDelete[config.itemIdField];
      if (!actualItemId) throw new Error(`Could not determine item ID for ${config.itemIdField}`);

      const cid = itemToDelete.company_id;
      const lid = itemToDelete.location_id;

      // Delete each lot item by its primary key ID (same method as individual lot delete)
      const lotsToDelete = itemToDelete.lots || [];
      for (const lot of lotsToDelete) {
        const { error: lotErr } = await supabase
          .from(config.itemsTable)
          .delete()
          .eq('id', lot.id);
        if (lotErr) throw lotErr;
      }

      // Also delete any remaining items via compound key (handles items not in lots array)
      await supabase
        .from(config.itemsTable)
        .delete()
        .eq('company_id', cid)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', lid);

      // Fetch aggregate record ID, then delete by primary key
      const { data: aggRow } = await supabase
        .from(config.inventoryTable)
        .select('id')
        .eq('company_id', cid)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', lid)
        .maybeSingle();
      if (aggRow) {
        const { error: aggError } = await supabase
          .from(config.inventoryTable)
          .delete()
          .eq('id', aggRow.id);
        if (aggError) throw aggError;
      }

      // Log deletion transaction
      const totalQty = Math.abs(parseFloat(itemToDelete.total_quantity) || 0);
      const itemName = itemToDelete.item_name || 'item';
      const txQtyFields = config.hasUnitOfMeasure
        ? { unit_quantity: -totalQty, total_quantity: -(Math.abs(parseFloat(itemToDelete.total_base_quantity) || totalQty)) }
        : { quantity: -totalQty };
      await supabase.from(config.transactionsTable).insert({
        company_id: cid,
        [config.itemIdField]: actualItemId,
        transaction_type: 'inventory_deletion',
        ...txQtyFields,
        location_id: lid,
        transaction_date: new Date().toISOString(),
        notes: `All inventory deleted — ${itemName} (${lotsToDelete.length} lot${lotsToDelete.length !== 1 ? 's' : ''})`
      });

      if (onRefresh) await onRefresh();
      setDeleteAggregateDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting aggregate inventory:', error);
      alert('Failed to delete inventory: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const updateAggregateAfterDelete = async (lotItem) => {
    // Chemicals: use the RPC which handles dual-quantity aggregate recalculation
    if (config.hasUnitOfMeasure) {
      await supabase.rpc('recalculate_raw_materials_aggregate_inventory', {
        p_company_id:      lotItem.company_id,
        p_raw_material_id: lotItem[config.itemIdField],
        p_location_id:     lotItem.location_id
      });
      return;
    }

    // Simple types: manually recalculate from remaining items
    const { data: remainingItems } = await supabase
      .from(config.itemsTable)
      .select('current_quantity')
      .eq('company_id', lotItem.company_id)
      .eq(config.itemIdField, lotItem[config.itemIdField])
      .eq('location_id', lotItem.location_id)
      .eq('is_active', true)
      .gt('current_quantity', 0);

    const totalQty = (remainingItems || []).reduce((sum, i) => sum + (i.current_quantity || 0), 0);

    if (totalQty > 0) {
      await supabase
        .from(config.inventoryTable)
        .update({
          current_quantity: totalQty,
          [config.lastUpdatedField || 'last_updated']: new Date().toISOString()
        })
        .eq('company_id', lotItem.company_id)
        .eq(config.itemIdField, lotItem[config.itemIdField])
        .eq('location_id', lotItem.location_id);
    } else {
      await supabase
        .from(config.inventoryTable)
        .delete()
        .eq('company_id', lotItem.company_id)
        .eq(config.itemIdField, lotItem[config.itemIdField])
        .eq('location_id', lotItem.location_id);
    }
  };

  const viewDocument = async (bucket, filePath) => {
    if (!bucket || !filePath) {
      console.error('Missing bucket or file path');
      return;
    }
    
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600);
      
      if (error) {
        console.error('Supabase storage error:', error);
        throw error;
      }
      
      if (!data || !data.signedUrl) {
        console.error('No signed URL returned. File may not exist:', filePath);
        return;
      }
      
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      console.error('Bucket:', bucket);
      console.error('File path:', filePath);
    }
  };

  const documentTypeMap = {
    invoice: { label: 'INV', color: 'primary', bucket: `${config.type}-transactions-invoices`, pathKey: 'invoice_file_path', icon: <ReceiptIcon fontSize="small" /> },
    po: { label: 'PO', color: 'secondary', bucket: `${config.type}-transactions-po`, pathKey: 'po_file_path', icon: <DescriptionIcon fontSize="small" />, sx: { bgcolor: '#00897b', color: 'white', '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#00695c' } } },
    coa: { label: 'COA', color: 'success', bucket: `${config.type}-transactions-coa`, pathKey: 'coa_file_path', icon: <AssignmentTurnedInIcon fontSize="small" /> },
    msds: { label: 'MSDS', color: 'warning', bucket: `${config.type}-transactions-msds`, pathKey: 'msds_file_path', icon: <ScienceIcon fontSize="small" /> },
    bol: { label: 'BOL', color: 'info', bucket: `${config.type}-transactions-bol`, pathKey: 'bol_file_path', icon: <LocalShippingIcon fontSize="small" /> },
    sds: { label: 'SDS', color: 'error', bucket: `${config.type}-transactions-sds`, pathKey: 'sds_file_path', icon: <ScienceIcon fontSize="small" /> }
  };

  const renderDocuments = (item) => {
    const availableDocs = [];
    const trans = item.source_transaction || {};

    Object.entries(documentTypeMap).forEach(([key, doc]) => {
      if (trans[doc.pathKey]) {
        availableDocs.push({ ...doc, key, path: trans[doc.pathKey] });
      }
    });

    if (availableDocs.length === 0) {
      return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>No documents</Typography>;
    }

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center" alignItems="center">
        {availableDocs.map(doc => (
          <Chip key={doc.key} label={doc.label} size="small" color={doc.sx ? undefined : doc.color} icon={doc.icon} clickable
            onClick={() => viewDocument(doc.bucket, doc.path)}
            sx={{ fontSize: '0.75rem', height: 24, ...(doc.sx || {}), '&:hover': { transform: 'scale(1.05)', ...(doc.sx?.['&:hover'] || {}) } }} />
        ))}
      </Stack>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h6" color="text.secondary">No inventory items found</Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer
        component={noContainerBorder ? 'div' : Paper}
        sx={noContainerBorder ? { overflow: 'visible' } : {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.15)}`,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {expandable && (
                <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Expand</TableCell>
              )}
              <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>{config.singularName || config.displayName.slice(0, -1)}</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Quantity</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Reorder Settings</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const isExpanded = expandedRows[`${item.item_id}-${item.location_id}`];
              const lotItems = item.lots || [];
              const qty = item.total_quantity || 0;
              const reorderPoint = item.reorder_point || 0;
              const reorderQty = item.reorder_quantity || 0;
              const isLowStock = reorderPoint > 0 && qty <= reorderPoint;

              return (
                <React.Fragment key={`${item.item_id}-${item.location_id}-${index}`}>
                  <TableRow
                    hover
                    sx={{
                      cursor: expandable ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      bgcolor: 'white',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s ease',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 3,
                        px: 3
                      }
                    }}
                  >
                    {expandable && (
                      <TableCell sx={{ py: 3, px: 3 }}>
                        <IconButton size="small" onClick={() => handleRowClick(`${item.item_id}-${item.location_id}`)}>
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 3, px: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', mb: 0.5 }}>{item.item_name}</Typography>
                      {item.item_sku && <Typography variant="caption" color="text.secondary">SKU: {item.item_sku}</Typography>}
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem', mb: 1 }}>
                        {formatQuantity(qty, config)} {config.unit || 'units'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {lotItems.length} lot{lotItems.length !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3 }}>
                      <Typography variant="body2">{item.location_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>Reorder Point: {formatQuantity(reorderPoint, config)}</Typography>
                      <Typography variant="body2">Reorder Qty: {formatQuantity(reorderQty, config)}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3 }} align="center">
                      {isLowStock ? (
                        <Chip label="Low Stock" size="small" color="warning" icon={<WarningIcon />} />
                      ) : (
                        <Chip label="In Stock" size="small" color="success" icon={<CheckCircleIcon />} />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3 }} align="center">
                      <IconButton size="small" color="primary" disabled={!canEdit} onClick={() => { setSelectedItem(item); setEditReorderDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" disabled={!canDelete} onClick={() => handleDeleteAggregate(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {expandable && (
                    <TableRow>
                      <TableCell sx={{ py: 0, px: 0, border: 'none' }} colSpan={7}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{
                            p: 4,
                            bgcolor: 'white',
                            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3, gap: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                                Lot/Batch Details ({(() => {
                                  const inventoryKey = `${item.item_id}-${item.location_id}`;
                                  const searchTerm = (lotBatchSearchTerms[inventoryKey] || '').toLowerCase();
                                  const filteredCount = searchTerm
                                    ? lotItems.filter(lotItem => {
                                        const lotNumber = (lotItem.lot_number || '').toLowerCase();
                                        const batchNumber = (lotItem.batch_number || '').toLowerCase();
                                        return lotNumber.includes(searchTerm) || batchNumber.includes(searchTerm);
                                      }).length
                                    : lotItems.length;
                                  return filteredCount;
                                })()}{(() => {
                                  const inventoryKey = `${item.item_id}-${item.location_id}`;
                                  const searchTerm = lotBatchSearchTerms[inventoryKey];
                                  return searchTerm ? ` of ${lotItems.length}` : '';
                                })()} lot{(() => {
                                  const inventoryKey = `${item.item_id}-${item.location_id}`;
                                  const searchTerm = (lotBatchSearchTerms[inventoryKey] || '').toLowerCase();
                                  const filteredCount = searchTerm
                                    ? lotItems.filter(lotItem => {
                                        const lotNumber = (lotItem.lot_number || '').toLowerCase();
                                        const batchNumber = (lotItem.batch_number || '').toLowerCase();
                                        return lotNumber.includes(searchTerm) || batchNumber.includes(searchTerm);
                                      }).length
                                    : lotItems.length;
                                  return filteredCount !== 1 ? 's' : '';
                                })()})
                              </Typography>

                              <TextField placeholder="Search lot/batch..." size="small"
                                value={lotBatchSearchTerms[`${item.item_id}-${item.location_id}`] || ''}
                                onChange={(e) => {
                                  setLotBatchSearchTerms(prev => ({
                                    ...prev,
                                    [`${item.item_id}-${item.location_id}`]: e.target.value
                                  }));
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                    </InputAdornment>
                                  )
                                }}
                                sx={{
                                  width: { xs: '100%', sm: '450px', md: '550px' },
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                    '& fieldset': {
                                      borderColor: alpha(theme.palette.grey[400], 0.8)
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  },
                                  '& .MuiInputBase-input': {
                                    fontSize: '0.9rem',
                                    py: 1.5
                                  }
                                }}
                              />
                            </Box>

                            {(() => {
                              const inventoryKey = `${item.item_id}-${item.location_id}`;
                              const searchTerm = (lotBatchSearchTerms[inventoryKey] || '').toLowerCase();
                              const lbPage = lotBatchPagination[inventoryKey]?.page ?? 0;
                              const lbRowsPerPage = lotBatchPagination[inventoryKey]?.rowsPerPage ?? LOT_BATCH_DEFAULT_ROWS;

                              const filteredLotItems = lotItems.filter(lotItem => {
                                if (!searchTerm) return true;
                                const lotNumber = (lotItem.lot_number || '').toLowerCase();
                                const batchNumber = (lotItem.batch_number || '').toLowerCase();
                                return lotNumber.includes(searchTerm) || batchNumber.includes(searchTerm);
                              });

                              const startIdx = lbPage * lbRowsPerPage;
                              const paginatedLotItems = filteredLotItems.slice(startIdx, startIdx + lbRowsPerPage);

                              return (
                                <>
                                  <TableContainer component={Paper} sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
                                    overflow: 'hidden'
                                  }}>
                                    <Table>
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Quantity</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Lot/Batch Details</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Vendor & Invoice</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="center">Status</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="center">Documents</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="center">Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {paginatedLotItems.map((lotItem, lotIndex) => (
                                      <TableRow
                                        key={`lot-item-${lotItem.id}-${lotIndex}`}
                                        hover
                                        sx={{
                                          transition: 'all 0.2s ease',
                                          '&:hover': {
                                            bgcolor: 'grey.50',
                                            transform: 'translateX(4px)',
                                            transition: 'all 0.2s ease',
                                            boxShadow: 'inset 4px 0 0 black'
                                          },
                                          '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.grey[50], 0.3) }
                                        }}
                                      >
                                        <TableCell sx={{ py: 3, px: 3 }}>
                                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 1 }}>
                                            {formatQuantity(lotItem.current_quantity || 0, config)} {config.unit || 'units'}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                            total
                                          </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 3, px: 3 }}>
                                          {lotItem.lot_number && (
                                            <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                                              Lot: {lotItem.lot_number}
                                            </Typography>
                                          )}
                                          {lotItem.batch_number && (
                                            <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                                              Batch: {lotItem.batch_number}
                                            </Typography>
                                          )}
                                          {lotItem.notes && cleanNotes(lotItem.notes) && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                              {cleanNotes(lotItem.notes)}
                                            </Typography>
                                          )}
                                        </TableCell>

                                        <TableCell sx={{ py: 3, px: 3 }}>
                                          {lotItem.vendor_info?.supplier_name && (
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 0.5, fontWeight: 500 }}>
                                              {lotItem.vendor_info.supplier_name}
                                            </Typography>
                                          )}
                                          {lotItem.vendor_info?.unit_cost && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Cost: ${parseFloat(lotItem.vendor_info.unit_cost).toFixed(2)}
                                            </Typography>
                                          )}
                                          {lotItem.vendor_info?.invoice_number && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Invoice #: {lotItem.vendor_info.invoice_number}
                                            </Typography>
                                          )}
                                          {lotItem.vendor_info?.invoice_date && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Invoice Date: {formatDate(lotItem.vendor_info.invoice_date)}
                                            </Typography>
                                          )}
                                          {lotItem.vendor_info?.po_number && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              PO #: {lotItem.vendor_info.po_number}
                                            </Typography>
                                          )}
                                          {lotItem.vendor_info?.po_date && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              PO Date: {formatDate(lotItem.vendor_info.po_date)}
                                            </Typography>
                                          )}
                                        </TableCell>

                                        <TableCell sx={{ py: 3, px: 3 }} align="center">
                                          <Chip label={lotItem.is_active ? 'Active' : 'Inactive'} size="small"
                                            color={lotItem.is_active ? 'success' : 'default'}
                                            icon={lotItem.is_active ? <CheckCircleIcon /> : undefined} />
                                        </TableCell>

                                        <TableCell sx={{ py: 3, px: 3 }} align="center">
                                          {renderDocuments(lotItem)}
                                        </TableCell>

                                        <TableCell sx={{ py: 3, px: 3 }} align="center">
                                          <IconButton size="small" color="primary" disabled={!canEdit} onClick={() => { setSelectedItem(lotItem); setEditLotDialogOpen(true); }}>
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton size="small" color="error" disabled={!canDelete} onClick={() => handleDeleteLot(lotItem)}>
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                  {filteredLotItems.length > 0 && (
                                    <PaginationController
                                      count={filteredLotItems.length}
                                      page={lbPage}
                                      onPageChange={(e, newPage) => setLotBatchPagination(prev => ({
                                        ...prev,
                                        [inventoryKey]: { page: newPage, rowsPerPage: prev[inventoryKey]?.rowsPerPage ?? LOT_BATCH_DEFAULT_ROWS }
                                      }))}
                                      rowsPerPage={lbRowsPerPage}
                                      onRowsPerPageChange={(val) => setLotBatchPagination(prev => ({
                                        ...prev,
                                        [inventoryKey]: { page: 0, rowsPerPage: val }
                                      }))}
                                      sx={{ py: 1.5, px: 2 }}
                                    />
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedItem && (
        <>
          <EditLotDialog open={editLotDialogOpen} onClose={() => { setEditLotDialogOpen(false); setSelectedItem(null); }}
            item={selectedItem} config={config} inventoryType={inventoryType} onSuccess={onRefresh} canDelete={canDelete} />
          <EditReorderDialog open={editReorderDialogOpen} onClose={() => { setEditReorderDialogOpen(false); setSelectedItem(null); }}
            item={selectedItem} config={config} inventoryType={inventoryType} onSuccess={onRefresh} />
        </>
      )}

      {/* Delete Lot/Batch Confirmation Dialog */}
      <Dialog
        open={deleteLotDialogOpen}
        onClose={() => !deleting && setDeleteLotDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 2,
          pb: 2
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
              Delete Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to delete this lot/batch item? If this is the last lot/batch for this {config.displayName.slice(0, -1).toLowerCase()} at this location, the entire inventory record will be removed.
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setDeleteLotDialogOpen(false)} 
            disabled={deleting}
            sx={{ mt: -1, mr: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {itemToDelete && (
            <Paper elevation={0} sx={{ 
              p: 2.5, 
              bgcolor: alpha(theme.palette.error.main, 0.04),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 2
            }}>
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1.5 }}>
                {itemToDelete.item_name || itemToDelete.name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>Location:</strong> {itemToDelete.location_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Stock:</strong> {formatQuantity(itemToDelete.current_quantity, config)}
                </Typography>
                {itemToDelete.lot_number && (
                  <Typography variant="body2">
                    <strong>Lot/Batch:</strong> {itemToDelete.lot_number} / {itemToDelete.batch_number}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}

          <Paper elevation={0} sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            borderRadius: 2,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start'
          }}>
            <WarningIcon sx={{ color: 'error.main', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone.
            </Typography>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={() => setDeleteLotDialogOpen(false)} 
            disabled={deleting}
            sx={{ 
              px: 3,
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteLot}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ 
              px: 3,
              borderRadius: 2
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Aggregate (All Lots) Confirmation Dialog */}
      <Dialog
        open={deleteAggregateDialogOpen}
        onClose={() => !deleting && setDeleteAggregateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 2,
          pb: 2
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
              Delete Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to delete this inventory item? This will also delete all related transactions.
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setDeleteAggregateDialogOpen(false)} 
            disabled={deleting}
            sx={{ mt: -1, mr: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {itemToDelete && (
            <Paper elevation={0} sx={{ 
              p: 2.5, 
              bgcolor: alpha(theme.palette.error.main, 0.04),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 2
            }}>
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1.5 }}>
                {itemToDelete.item_name || itemToDelete.name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>Location:</strong> {itemToDelete.location_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Stock:</strong> {formatQuantity(itemToDelete.total_quantity || itemToDelete.current_quantity, config)}
                </Typography>
              </Box>
            </Paper>
          )}

          <Paper elevation={0} sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            borderRadius: 2,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start'
          }}>
            <WarningIcon sx={{ color: 'error.main', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone.
            </Typography>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={() => setDeleteAggregateDialogOpen(false)} 
            disabled={deleting}
            sx={{ 
              px: 3,
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteAggregate}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ 
              px: 3,
              borderRadius: 2
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
